/**
 * События продуктовой воронки.
 *
 * Отдельный канал от PostHog: воронку внутреннего дашборда строит наш
 * analytics-микросервис по своим данным, чтобы цифры сходились с заказами
 * и пользователями из тех же баз.
 */

import { deviceHash } from "@shared/utils/device";

const ANALYTICS_URL = import.meta.env.VITE_ANALYTICS_URL || "";
const ANONYMOUS_ID_KEY = "phodo_anonymous_id";
const TOUCHPOINT_KEY = "phodo_touchpoint";
const REFERRAL_KEY = "phodo_referral";

export type FunnelEventName =
  | "first_touch"
  | "app_open"
  | "signup_completed"
  | "map_view"
  | "service_selected"
  | "file_uploaded"
  | "cart_view"
  | "order_created"
  | "order_paid"
  | "order_issued";

/**
 * Атрибуция, переданная сайтом link стартовым параметром мини-приложения.
 *
 * Формат: `a<сессия>-t<материал>-r<реф>` — ключ это первая буква, значение идёт
 * сразу за ней, пары разделены `-`. Разделителя между ключом и значением нет
 * намеренно: Telegram допускает в стартовом параметре только `A-Za-z0-9_-`
 * (`/^[\w-]{0,512}$/`), и любой другой символ параметр бы испортил.
 */
export function parseStartParam(startParam: string | undefined | null): boolean {
  if (!startParam) {
    return false;
  }

  let applied = false;

  const keys: Record<string, string> = {
    a: ANONYMOUS_ID_KEY,
    t: TOUCHPOINT_KEY,
    r: REFERRAL_KEY,
  };

  for (const part of startParam.split("-")) {
    const storageKey = keys[part[0]];
    const value = part.slice(1);
    if (!storageKey || !value) {
      continue;
    }
    localStorage.setItem(storageKey, value);
    applied = true;
  }

  return applied;
}

/**
 * Точка входа и первое касание.
 *
 * Человек попадает в приложение не только с сайта link: он может открыть бота
 * напрямую, отсканировать QR на корпусе аппарата или прийти из поиска. Воронка
 * должна начинаться там, где человека увидели впервые, иначе все, кто пришёл
 * мимо link, в неё не попадают вовсе.
 *
 * Если атрибуция приехала стартовым параметром — первое касание уже засчитал
 * сайт link, второй раз его слать нельзя. Если сессии ещё не было — это прямой
 * вход, и первое касание принадлежит приложению.
 */
export function markEntry(startParam: string | undefined | null): void {
  const hadSession = Boolean(localStorage.getItem(ANONYMOUS_ID_KEY));
  const fromLink = parseStartParam(startParam);

  if (!fromLink && !hadSession) {
    trackFunnel("first_touch", null, { entry: "miniapp_direct" });
  }
}

function getAnonymousId(): string {
  const existing = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (existing) {
    return existing;
  }

  const created =
    globalThis.crypto?.randomUUID?.().replace(/-/g, "") ??
    `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  localStorage.setItem(ANONYMOUS_ID_KEY, created);
  return created;
}

/** Реферальный код из атрибуции — нужен и рефералке при регистрации. */
export function getReferralCode(): string | null {
  return localStorage.getItem(REFERRAL_KEY);
}

/** Код материала, с которого человек пришёл. */
export function getTouchpointCode(): string | null {
  return localStorage.getItem(TOUCHPOINT_KEY);
}

function post(path: string, body: unknown): void {
  if (!ANALYTICS_URL) {
    return;
  }

  const url = `${ANALYTICS_URL.replace(/\/$/, "")}${path}`;
  const payload = JSON.stringify(body);

  try {
    // text/plain делает запрос простым: без предварительного запроса CORS,
    // который при уходе со страницы не успевает завершиться.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([payload], { type: "text/plain;charset=UTF-8" }));
      return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
      body: payload,
      keepalive: true,
    });
  } catch {
    // Событие потеряно — работа приложения важнее.
  }
}

/** Записать шаг воронки. */
export function trackFunnel(
  name: FunnelEventName,
  userId?: number | null,
  props?: Record<string, unknown>
): void {
  post("/events/track", {
    events: [
      {
        name,
        anonymous_id: getAnonymousId(),
        user_id: userId ?? null,
        touchpoint_code: getTouchpointCode(),
        referral_code: getReferralCode(),
        source: "miniapp",
        entry_point: entryPoint(),
        device_hash: deviceHash(),
        props,
      },
    ],
  });
}

/**
 * Как человек попал в продукт. Материал из ссылки означает переход с печатного
 * носителя, реферальный код — приглашение, иначе прямой вход в приложение.
 */
function entryPoint(): string {
  if (getReferralCode()) return "referral";
  if (getTouchpointCode()) return "link";
  return "miniapp_direct";
}

/** Связать анонимную сессию с пользователем — после входа. */
export function identifyFunnel(userId: number): void {
  post("/events/identify", {
    anonymous_id: getAnonymousId(),
    user_id: userId,
  });
}
